import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Suche } from '../../icons';
import '../ModalDeviceSearch/ModalDeviceSearch.css';

/**
 * Reusable combobox with inline filtering, keyboard navigation, clear button,
 * and an optional "changed" highlight. Used in edit forms across multiple pages.
 *
 * Props:
 *   id            – unique suffix for generated ids/aria attributes
 *   label         – visible label text
 *   required      – shows * indicator
 *   placeholder   – input placeholder (defaults to label)
 *   inputValue    – controlled text value
 *   onInputChange – called with new text on every keystroke
 *   options       – [{ value, label }] – full list to filter from
 *   onSelect      – called with the chosen option object
 *   onClear       – called when the ✖ button is pressed
 *   disabled      – disables the field
 *   changed       – when true, highlights the field and shows "• geändert" badge
 *   invalid       – when true, marks the input with the error border
 *   focusNextOnSelect – moves focus to the next form field after a selection
 *   onBlur        – called when focus leaves the whole field (input, clear button, list)
 */
export function SearchDropdown({
  id,
  label,
  required,
  placeholder,
  inputValue,
  onInputChange,
  options,
  onSelect,
  onClear,
  disabled,
  changed,
  invalid,
  focusNextOnSelect,
  onBlur,
}) {
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(-1);
  const inputRef = useRef(null);
  const wrapperRef = useRef(null);
  const listRef = useRef(null);

  const filtered = useMemo(
    () => options.filter(o => !inputValue || o.label.toLowerCase().includes(inputValue.toLowerCase())),
    [options, inputValue]
  );

  useEffect(() => {
    if (!listRef.current || highlighted < 0) return;
    const items = listRef.current.querySelectorAll('[role="option"]');
    items[highlighted]?.scrollIntoView({ block: 'nearest' });
  }, [highlighted]);

  const selectOption = (option) => {
    onSelect(option);
    setOpen(false);
    setHighlighted(-1);
    if (!focusNextOnSelect) return;
    // Deferred so the re-render can update disabled states first
    setTimeout(() => {
      const fields = Array.from(document.querySelectorAll(
        'input:not([disabled]), textarea:not([disabled]), select:not([disabled])'
      )).filter(el => el.type !== 'radio' || el.checked);
      const idx = fields.indexOf(inputRef.current);
      for (let i = idx + 1; idx >= 0 && i < fields.length; i++) {
        if (!wrapperRef.current?.contains(fields[i])) { fields[i].focus(); break; }
      }
    }, 0);
  };

  const handleKeyDown = (e) => {
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        setOpen(true);
        setHighlighted(0);
      }
      return;
    }
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlighted(h => Math.min(h + 1, filtered.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlighted(h => Math.max(h - 1, 0)); }
    else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlighted >= 0 && highlighted < filtered.length) selectOption(filtered[highlighted]);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
      setHighlighted(-1);
    }
  };

  const handleBlur = (e) => {
    if (!wrapperRef.current?.contains(e.relatedTarget)) {
      setOpen(false);
      setHighlighted(-1);
      onBlur?.();
    }
  };

  return (
    <div
      className={`modal-device-search-wrapper${changed ? ' changed' : ''}`}
      ref={wrapperRef}
      onBlur={handleBlur}
    >
      <label className="modal-device-search-label" htmlFor={`sd-input-${id}`}>
        {label}{required && <span className="required">*</span>}
        {changed && <span className="changed-badge" aria-label="Geändert">• geändert</span>}
      </label>
      <span className="modal-device-search-icon" aria-hidden="true"><Suche /></span>
      <input
        ref={inputRef}
        type="text"
        id={`sd-input-${id}`}
        className={`modal-device-search-input${invalid ? ' modal-device-search-input--invalid' : ''}`}
        value={inputValue}
        onChange={e => { onInputChange(e.target.value); setOpen(true); setHighlighted(-1); }}
        onFocus={() => !disabled && setOpen(true)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder ?? label}
        disabled={disabled}
        autoComplete="off"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open && !disabled}
        aria-controls={`sd-list-${id}`}
        aria-activedescendant={highlighted >= 0 && open ? `sd-opt-${id}-${highlighted}` : undefined}
      />
      {inputValue && !disabled && (
        <button
          type="button"
          className="modal-device-search-clear"
          aria-label="Auswahl zurücksetzen"
          tabIndex={0}
          onMouseDown={e => { e.preventDefault(); onClear(); inputRef.current?.focus(); }}
          onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClear(); inputRef.current?.focus(); } }}
        >✖</button>
      )}
      {open && !disabled && filtered.length > 0 && (
        <div className="modal-device-search-suggestions" role="listbox" id={`sd-list-${id}`} ref={listRef}>
          {filtered.map((o, idx) => (
            <div
              key={o.value}
              id={`sd-opt-${id}-${idx}`}
              className={`modal-device-search-suggestion${idx === highlighted ? ' highlighted' : ''}`}
              role="option"
              aria-selected={idx === highlighted}
              onMouseDown={() => selectOption(o)}
              onMouseEnter={() => setHighlighted(idx)}
            >
              {o.label}
            </div>
          ))}
        </div>
      )}
      {open && !disabled && filtered.length === 0 && inputValue && (
        <div className="modal-device-search-suggestions" id={`sd-list-${id}`}>
          <div className="modal-device-search-suggestion-no-results">Keine Ergebnisse</div>
        </div>
      )}
    </div>
  );
}
